import React, { useEffect, useState } from 'react';
import {
  TrendingUp, TrendingDown, DollarSign, Users, Truck,
  ArrowUpCircle, ArrowDownCircle, Search, X, Plus,
  BarChart3, Activity, CreditCard
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Modal, Button, FormField, Input, Select, PageHeader, Badge } from '../../components/UI';
import Breadcrumb from '../../components/Breadcrumb';
import FundingSourceModal from '../../components/FundingSourceModal';
import JalaliDatePicker, { JalaliDateRangeFilter } from '../../components/JalaliDatePicker';
import ConfirmModal from '../../components/ConfirmModal';
import { accountingService } from '../../api/services';
import { formatCurrency, formatNumber } from '../../utils/helpers';
import { toJalali } from '../../utils/jalali';
import toast from 'react-hot-toast';

// ─── Stat Card ────────────────────────────────────────────────────────────────
function ACard({ label, value, sub, color='blue', icon: Icon }) {
  const colors = { blue:'bg-blue-50 text-blue-700', green:'bg-green-50 text-green-700', red:'bg-red-50 text-red-700', orange:'bg-orange-50 text-orange-700', purple:'bg-purple-50 text-purple-700' };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-gray-500 font-medium">{label}</p>
        {Icon && <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors[color]}`}><Icon size={15}/></div>}
      </div>
      <p className={`text-xl font-bold ${color==='red'?'text-red-700':color==='green'?'text-green-700':color==='orange'?'text-orange-700':color==='purple'?'text-purple-700':'text-gray-900'}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ─── Payment Modal ─────────────────────────────────────────────────────────────
function PaymentModal({ isOpen, onClose, target, type, onSuccess }) {
  const [form, setForm] = useState({ amount:'', method:'cash', reference:'', notes:'' });
  const [invoices, setInvoices]   = useState([]);
  const [payments, setPayments]   = useState([]);
  const [saving, setSaving]       = useState(false);
  const [fundingOpen, setFundingOpen] = useState(false);
  const [fundingSource, setFundingSource] = useState('pharmacy_cash');
  const [fundingLabel,  setFundingLabel]  = useState('Pharmacy Cash');
  const [selInvoice, setSelInv]   = useState('');

  useEffect(() => {
    if (!target || !isOpen) return;
    setForm({ amount:'', method:'cash', reference:'', notes:'' });
    setSelInv('');
    if (type === 'customer') {
      accountingService.getCustomerInvoices(target._id).then(r => setInvoices(r.data.data||[])).catch(()=>{});
      accountingService.getCustomerPayments(target._id).then(r => setPayments(r.data.data||[])).catch(()=>{});
    } else {
      accountingService.getSupplierInvoices(target._id).then(r => setInvoices(r.data.data||[])).catch(()=>{});
      accountingService.getSupplierPayments(target._id).then(r => setPayments(r.data.data||[])).catch(()=>{});
    }
  }, [target, isOpen, type]);

  const handlePay = async () => {
    if (!form.amount || Number(form.amount) <= 0) return toast.error('Enter a valid amount');
    setSaving(true);
    try {
      const payload = { ...form, amount: Number(form.amount), ...(selInvoice && (type==='customer'?{sale:selInvoice}:{purchase:selInvoice})) };
      if (type === 'customer') await accountingService.addCustomerPayment(target._id, payload);
      else await accountingService.addSupplierPayment(target._id, payload);
      toast.success('Payment recorded');
      onSuccess();
      onClose();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  if (!target) return null;
  const debt = target.totalDebt || 0;
  const name = target.name;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Account — ${name}`} size="lg">
      <div className="space-y-4">
        {/* Debt summary */}
        <div className={`flex justify-between items-center p-4 rounded-xl border ${debt>0?'bg-red-50 border-red-100':'bg-green-50 border-green-100'}`}>
          <div>
            <p className="text-xs text-gray-500">{type==='customer'?'Customer Debt':'Supplier Debt'}</p>
            <p className={`text-2xl font-bold ${debt>0?'text-red-700':'text-green-700'}`}>{formatCurrency(debt)}</p>
          </div>
          <Badge color={debt>0?'red':'green'}>{debt>0?'Has Debt':'Cleared'}</Badge>
        </div>

        {/* Unpaid invoices */}
        {invoices.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Unpaid Invoices</p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {invoices.map(inv => (
                <label key={inv._id} className={`flex items-center justify-between p-2.5 rounded-lg border cursor-pointer transition-colors ${selInvoice===inv._id?'border-blue-400 bg-blue-50':'border-gray-100 hover:bg-gray-50'}`}>
                  <div className="flex items-center gap-2">
                    <input type="radio" name="invoice" value={inv._id} checked={selInvoice===inv._id} onChange={e=>setSelInv(e.target.value)} className="text-blue-600"/>
                    <span className="text-xs font-mono text-blue-700">{inv.invoiceNumber||inv.poNumber}</span>
                  </div>
                  <div className="text-right text-xs">
                    <p className="font-semibold text-gray-800">{formatCurrency(inv.totalAmount)}</p>
                    <p className="text-gray-400">Paid: {formatCurrency(inv.paidAmount)}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Payment form */}
        {debt > 0 && (
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <p className="text-sm font-semibold text-gray-700 mb-3">Record Payment</p>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Amount (max: {formatCurrency(debt)})" required>
                <Input type="number" step="0.01" max={debt} value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})} placeholder="0.00"/>
              </FormField>
              <FormField label="Method">
                <Select value={form.method} onChange={e=>setForm({...form,method:e.target.value})}>
                  <option value="cash">Cash</option>
                  <option value="bank">Bank Transfer</option>
                  <option value="card">Card</option>
                  <option value="mobile">Mobile</option>
                </Select>
              </FormField>
              <FormField label="Reference">
                <Input value={form.reference} onChange={e=>setForm({...form,reference:e.target.value})} placeholder="Receipt / ref no."/>
              </FormField>
              <FormField label="Notes">
                <Input value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} placeholder="Notes"/>
              </FormField>
            </div>
            <div className="flex gap-2 mt-3 items-center">
              <button type="button" onClick={()=>setForm({...form,amount:String(debt)})}
                className="text-xs px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100">Pay Full ({formatCurrency(debt)})</button>
              <Button onClick={handlePay} loading={saving} className="ml-auto bg-green-600 hover:bg-green-700 text-white">
                <DollarSign size={15}/> Record
              </Button>
            </div>
          </div>
        )}

        {/* Payment history */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Payment History ({payments.length})</p>
          {payments.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">No payments yet</p> : (
            <div className="border border-gray-100 rounded-lg overflow-hidden max-h-52 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0"><tr>
                  <th className="text-left px-3 py-2 font-semibold text-gray-400">Amount</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-400">Method</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-400">Ref.</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-400">By</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-400">Date</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {payments.map(p=>(
                    <tr key={p._id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-semibold text-green-700">{formatCurrency(p.amount)}</td>
                      <td className="px-3 py-2 capitalize text-gray-600">{p.method}</td>
                      <td className="px-3 py-2 text-gray-400">{p.reference||'-'}</td>
                      <td className="px-3 py-2 text-gray-400">{p.createdBy?.name||'-'}</td>
                      <td className="px-3 py-2 text-gray-400">{toJalali(p.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ─── Main AccountingPage ──────────────────────────────────────────────────────
export default function AccountingPage() {
  const [tab, setTab]         = useState('dashboard');
  const [summary, setSummary] = useState(null);
  const [plData, setPlData]   = useState([]);
  const [cfData, setCfData]   = useState([]);
  const [customers, setCustomers] = useState([]);
  const [custTotal, setCustTotal] = useState(0);
  const [suppliers, setSuppliers] = useState([]);
  const [suppTotal, setSuppTotal] = useState(0);
  const [expenses,  setExpenses]  = useState([]);
  const [expTotal,  setExpTotal]  = useState(0);
  const [loading, setLoading] = useState(false);

  const [period,   setPeriod]   = useState('monthly');
  const [dateFrom, setDF]       = useState('');
  const [dateTo,   setDT]       = useState('');
  const [custSearch, setCS]     = useState('');
  const [custDebt,   setCD]     = useState('');
  const [suppSearch, setSS]     = useState('');
  const [suppDebt,   setSD]     = useState('');
  const [expCat,     setEC]     = useState('');
  const [page, setPage]         = useState(1);

  const [payTarget, setPayTarget] = useState(null);
  const [payType,   setPayType]   = useState('customer');

  const [expModal, setExpModal]   = useState(false);
  const [fundingModalOpen, setFMOpen] = useState(false);
  const [editExp,  setEditExp]    = useState(null);
  const [delExp,   setDelExp]     = useState(null);
  const [expForm,  setExpForm]    = useState({ category:'', amount:'', description:'', date:'' });
  const [savingExp,setSavingExp]  = useState(false);
  const [deletingExp, setDeleting]= useState(false);

  const EXP_CATS = ['Rent','Electricity','Internet','Salaries','Transportation','Equipment','Supplies','Maintenance','Marketing','Other'];

  useEffect(() => { fetchSummary(); }, [dateFrom, dateTo]); // eslint-disable-line
  useEffect(() => {
    if (tab==='profit-loss') fetchPL();
    if (tab==='cash-flow')   fetchCF();
    if (tab==='customers')   fetchCustomers();
    if (tab==='suppliers')   fetchSuppliers();
    if (tab==='expenses')    fetchExpenses();
  }, [tab, period, page, custSearch, custDebt, suppSearch, suppDebt, expCat, dateFrom, dateTo]); // eslint-disable-line


  const handleFundingSelect = (key, label) => {
    setFundingSource(key);
    setFundingLabel(label);
    setFundingOpen(false);
    crud.openCreate();
  };

  const fetchSummary = async () => {
    try { const r = await accountingService.getSummary({ from: dateFrom||undefined, to: dateTo||undefined }); setSummary(r.data.data); } catch {}
  };
  const fetchPL = async () => {
    setLoading(true);
    try { const r = await accountingService.getProfitLoss({ period, from: dateFrom||undefined, to: dateTo||undefined }); setPlData(r.data.data||[]); } catch {}
    finally { setLoading(false); }
  };
  const fetchCF = async () => {
    setLoading(true);
    try { const r = await accountingService.getCashFlow({ period, from: dateFrom||undefined, to: dateTo||undefined }); setCfData(r.data.data||[]); } catch {}
    finally { setLoading(false); }
  };
  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const r = await accountingService.getCustomerAccounts({ page, limit:20, search:custSearch||undefined, hasDebt:custDebt||undefined });
      setCustomers(r.data.data||[]); setCustTotal(r.data.pagination?.total||0);
    } catch {} finally { setLoading(false); }
  };
  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const r = await accountingService.getSupplierAccounts({ page, limit:20, search:suppSearch||undefined, hasDebt:suppDebt||undefined });
      setSuppliers(r.data.data||[]); setSuppTotal(r.data.pagination?.total||0);
    } catch {} finally { setLoading(false); }
  };
  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const r = await accountingService.getExpenses({ page, limit:20, category:expCat||undefined, from:dateFrom||undefined, to:dateTo||undefined });
      setExpenses(r.data.data||[]); setExpTotal(r.data.pagination?.total||0);
    } catch {} finally { setLoading(false); }
  };

  const handleSaveExp = async () => {
    if (!expForm.category||!expForm.amount) return toast.error('Category and amount required');
    setSavingExp(true);
    try {
      if (editExp) { await accountingService.updateExpense(editExp._id, expForm); toast.success('Updated'); }
      else { await accountingService.createExpense(expForm); toast.success('Expense recorded'); }
      setExpModal(false); setEditExp(null);
      fetchExpenses(); fetchSummary();
    } catch (e) { toast.error(e.response?.data?.message||'Failed'); }
    finally { setSavingExp(false); }
  };

  const handleDelExp = async () => {
    setDeleting(true);
    try { await accountingService.deleteExpense(delExp._id); toast.success('Deleted'); setDelExp(null); fetchExpenses(); fetchSummary(); }
    catch (e) { toast.error('Failed'); }
    finally { setDeleting(false); }
  };

  const TABS = [
    { key:'dashboard',   label:'Dashboard' },
    { key:'profit-loss', label:'P&L' },
    { key:'cash-flow',   label:'Cash Flow' },
    { key:'customers',   label:'Receivables' },
    { key:'suppliers',   label:'Payables' },
    { key:'expenses',    label:'Expenses' },
  ];

  // ─── Shared filter bar ───────────────────────────────────────────────────
  const FilterBar = () => (
    <div className="flex flex-wrap gap-3 items-center bg-white rounded-xl border border-gray-200 p-3 mb-4">
      {['profit-loss','cash-flow','dashboard'].includes(tab) && (
        <Select value={period} onChange={e=>setPeriod(e.target.value)} className="w-28">
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
        </Select>
      )}
      <JalaliDatePicker value={dateFrom} onChange={v=>setDF(v)} placeholder="From date" className="w-40"/>
      <JalaliDatePicker value={dateTo} onChange={v=>setDT(v)} placeholder="To date" className="w-40"/>
      {(dateFrom||dateTo) && <button onClick={()=>{setDF('');setDT('');}} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"><X size={12}/>Clear</button>}
    </div>
  );

  // ─── Pagination ──────────────────────────────────────────────────────────
  const Pagination = ({ total: t }) => {
    if (t <= 20) return null;
    return (
      <div className="flex justify-between items-center px-4 py-3 border-t border-gray-100 bg-white">
        <p className="text-xs text-gray-500">{((page-1)*20)+1}–{Math.min(page*20,t)} of {t}</p>
        <div className="flex gap-1">
          <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} className="px-3 py-1.5 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Prev</button>
          <span className="px-3 py-1.5 text-xs text-gray-600">Page {page}</span>
          <button onClick={()=>setPage(p=>p+1)} disabled={page*20>=t} className="px-3 py-1.5 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next</button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <Breadcrumb/>
      <PageHeader title="Accounting" subtitle="Financial management and reports"
        actions={tab==='expenses' ? <Button onClick={()=>setFMOpen(true)}><Plus size={16}/> Add Expense</Button> : null}
      />

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-4 bg-white border border-gray-200 rounded-xl p-1.5">
        {TABS.map(t=>(
          <button key={t.key} onClick={()=>{setTab(t.key);setPage(1);}}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab===t.key?'bg-blue-600 text-white shadow-sm':'text-gray-600 hover:bg-gray-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      <FilterBar/>

      {/* ── DASHBOARD ──────────────────────────────────────────────────────── */}
      {tab==='dashboard' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <ACard label="Total Revenue"     value={formatCurrency(summary?.revenue||0)}          color="green"  icon={TrendingUp}/>
            <ACard label="Gross Profit"      value={formatCurrency(summary?.grossProfit||0)}       color="blue"   icon={BarChart3}  sub={`Net: ${formatCurrency(summary?.netProfit||0)}`}/>
            <ACard label="Total Expenses"    value={formatCurrency(summary?.expenses||0)}          color="orange" icon={TrendingDown}/>
            <ACard label="Cost of Goods"     value={formatCurrency(summary?.cogs||0)}              color="purple" icon={Activity}/>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <ACard label="Total Receivables" value={formatCurrency(summary?.totalReceivables||0)}  color="red"    icon={Users}
              sub={`${summary?.debtorCount||0} debtors`}/>
            <ACard label="Collected"         value={formatCurrency(summary?.collected||0)}         color="green"  icon={ArrowDownCircle}
              sub={`Uncollected: ${formatCurrency(summary?.uncollected||0)}`}/>
            <ACard label="Total Payables"    value={formatCurrency(summary?.totalPayables||0)}     color="red"    icon={Truck}
              sub={`${summary?.creditorCount||0} creditors`}/>
            <ACard label="Sales Count"       value={formatNumber(summary?.salesCount||0)}          color="blue"   icon={CreditCard}
              sub={`${summary?.purchasesCount||0} purchases`}/>
          </div>
          {/* Net Profit highlight */}
          <div className={`rounded-xl border-2 p-5 ${(summary?.netProfit||0)>=0?'border-green-200 bg-green-50':'border-red-200 bg-red-50'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Net Profit / Loss</p>
                <p className={`text-3xl font-bold ${(summary?.netProfit||0)>=0?'text-green-700':'text-red-700'}`}>
                  {formatCurrency(summary?.netProfit||0)}
                </p>
              </div>
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${(summary?.netProfit||0)>=0?'bg-green-100':'bg-red-100'}`}>
                {(summary?.netProfit||0)>=0 ? <TrendingUp size={28} className="text-green-600"/> : <TrendingDown size={28} className="text-red-600"/>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── PROFIT & LOSS ──────────────────────────────────────────────────── */}
      {tab==='profit-loss' && (
        <div className="space-y-4">
          {loading ? <div className="h-64 bg-gray-100 rounded-xl animate-pulse"/> : (
            <>
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <p className="text-sm font-semibold text-gray-700 mb-3">Revenue vs COGS vs Expenses</p>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={plData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                    <XAxis dataKey="period" tick={{fontSize:10}} tickLine={false}/>
                    <YAxis tick={{fontSize:10}} tickLine={false} axisLine={false}/>
                    <Tooltip formatter={v=>formatCurrency(v)}/>
                    <Legend/>
                    <Bar dataKey="revenue"  name="Revenue"  fill="#22c55e" radius={[3,3,0,0]}/>
                    <Bar dataKey="cogs"     name="COGS"     fill="#f97316" radius={[3,3,0,0]}/>
                    <Bar dataKey="expenses" name="Expenses" fill="#ef4444" radius={[3,3,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b"><tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Period</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-green-600">Revenue</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-orange-500">COGS</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-blue-600">Gross Profit</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-red-500">Expenses</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-700">Net Profit</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {plData.length===0?<tr><td colSpan={6} className="text-center py-8 text-gray-400">No data</td></tr>:
                      plData.map(r=>(
                        <tr key={r.period} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-700">{r.period}</td>
                          <td className="px-4 py-3 text-right text-green-700 font-medium">{formatCurrency(r.revenue)}</td>
                          <td className="px-4 py-3 text-right text-orange-500">{formatCurrency(r.cogs)}</td>
                          <td className="px-4 py-3 text-right text-blue-700 font-medium">{formatCurrency(r.grossProfit)}</td>
                          <td className="px-4 py-3 text-right text-red-500">{formatCurrency(r.expenses)}</td>
                          <td className={`px-4 py-3 text-right font-bold ${r.netProfit>=0?'text-green-700':'text-red-600'}`}>{formatCurrency(r.netProfit)}</td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── CASH FLOW ─────────────────────────────────────────────────────── */}
      {tab==='cash-flow' && (
        <div className="space-y-4">
          {loading ? <div className="h-64 bg-gray-100 rounded-xl animate-pulse"/> : (
            <>
              <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <p className="text-sm font-semibold text-gray-700 mb-3">Cash Inflow vs Outflow</p>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={cfData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                    <XAxis dataKey="period" tick={{fontSize:10}} tickLine={false}/>
                    <YAxis tick={{fontSize:10}} tickLine={false} axisLine={false}/>
                    <Tooltip formatter={v=>formatCurrency(v)}/>
                    <Legend/>
                    <Line type="monotone" dataKey="inflow"  name="Inflow"  stroke="#22c55e" strokeWidth={2} dot={false}/>
                    <Line type="monotone" dataKey="outflow" name="Outflow" stroke="#ef4444" strokeWidth={2} dot={false}/>
                    <Line type="monotone" dataKey="net"     name="Net"     stroke="#3b82f6" strokeWidth={2} dot={false} strokeDasharray="4 2"/>
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b"><tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Period</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-green-600">Inflow</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-red-500">Outflow</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-blue-600">Net</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {cfData.map(r=>(
                      <tr key={r.period} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-700">{r.period}</td>
                        <td className="px-4 py-3 text-right text-green-700">{formatCurrency(r.inflow)}</td>
                        <td className="px-4 py-3 text-right text-red-500">{formatCurrency(r.outflow)}</td>
                        <td className={`px-4 py-3 text-right font-bold ${r.net>=0?'text-blue-700':'text-red-600'}`}>{formatCurrency(r.net)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── CUSTOMER ACCOUNTS (Receivables) ───────────────────────────────── */}
      {tab==='customers' && (
        <div>
          <div className="flex flex-wrap gap-3 mb-3">
            <div className="relative flex-1 min-w-36">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
              <input value={custSearch} onChange={e=>{setCS(e.target.value);setPage(1);}} placeholder="Search customer..."
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
            <Select value={custDebt} onChange={e=>{setCD(e.target.value);setPage(1);}} className="w-36">
              <option value="">All Customers</option>
              <option value="true">Has Debt</option>
              <option value="false">Cleared</option>
            </Select>
          </div>
          <div className="hidden md:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b"><tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Customer</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Phone</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-red-500">Debt</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Status</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500">Action</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-50">
                {loading?Array(5).fill(0).map((_,i)=><tr key={i}><td colSpan={5} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse"/></td></tr>):
                  customers.length===0?<tr><td colSpan={5} className="text-center py-10 text-gray-400">No customers found</td></tr>:
                  customers.map(c=>(
                    <tr key={c._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-800">{c.name}</td>
                      <td className="px-4 py-3 text-gray-500">{c.phone||'-'}</td>
                      <td className={`px-4 py-3 text-right font-bold ${c.totalDebt>0?'text-red-600':'text-green-600'}`}>{formatCurrency(c.totalDebt||0)}</td>
                      <td className="px-4 py-3"><Badge color={c.totalDebt>0?'red':'green'}>{c.totalDebt>0?'Debtor':'Cleared'}</Badge></td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={()=>{setPayTarget(c);setPayType('customer');}}
                          className="px-3 py-1.5 text-xs rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium">
                          <DollarSign size={12} className="inline mr-1"/>Account
                        </button>
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
            <Pagination total={custTotal}/>
          </div>
          {/* Mobile */}
          <div className="md:hidden space-y-3">
            {customers.map(c=>(
              <div key={c._id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm flex justify-between items-center">
                <div><p className="font-semibold text-gray-800">{c.name}</p><p className="text-xs text-gray-400">{c.phone}</p></div>
                <div className="text-right">
                  <p className={`font-bold ${c.totalDebt>0?'text-red-600':'text-green-600'}`}>{formatCurrency(c.totalDebt||0)}</p>
                  <button onClick={()=>{setPayTarget(c);setPayType('customer');}} className="text-xs text-blue-600 mt-1">View Account</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── SUPPLIER ACCOUNTS (Payables) ──────────────────────────────────── */}
      {tab==='suppliers' && (
        <div>
          <div className="flex flex-wrap gap-3 mb-3">
            <div className="relative flex-1 min-w-36">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
              <input value={suppSearch} onChange={e=>{setSS(e.target.value);setPage(1);}} placeholder="Search supplier..."
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
            <Select value={suppDebt} onChange={e=>{setSD(e.target.value);setPage(1);}} className="w-36">
              <option value="">All Suppliers</option>
              <option value="true">Has Debt</option>
              <option value="false">Cleared</option>
            </Select>
          </div>
          <div className="hidden md:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b"><tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Supplier</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Phone</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-red-500">Debt</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Status</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500">Action</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-50">
                {loading?Array(5).fill(0).map((_,i)=><tr key={i}><td colSpan={5} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse"/></td></tr>):
                  suppliers.length===0?<tr><td colSpan={5} className="text-center py-10 text-gray-400">No suppliers found</td></tr>:
                  suppliers.map(s=>(
                    <tr key={s._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3"><p className="font-medium text-gray-800">{s.name}</p><p className="text-xs text-gray-400">{s.company}</p></td>
                      <td className="px-4 py-3 text-gray-500">{s.phone||'-'}</td>
                      <td className={`px-4 py-3 text-right font-bold ${s.totalDebt>0?'text-red-600':'text-green-600'}`}>{formatCurrency(s.totalDebt||0)}</td>
                      <td className="px-4 py-3"><Badge color={s.totalDebt>0?'red':'green'}>{s.totalDebt>0?'Creditor':'Cleared'}</Badge></td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={()=>{setPayTarget(s);setPayType('supplier');}}
                          className="px-3 py-1.5 text-xs rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium">
                          <DollarSign size={12} className="inline mr-1"/>Account
                        </button>
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
            <Pagination total={suppTotal}/>
          </div>
          <div className="md:hidden space-y-3">
            {suppliers.map(s=>(
              <div key={s._id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm flex justify-between items-center">
                <div><p className="font-semibold text-gray-800">{s.name}</p><p className="text-xs text-gray-400">{s.company}</p></div>
                <div className="text-right">
                  <p className={`font-bold ${s.totalDebt>0?'text-red-600':'text-green-600'}`}>{formatCurrency(s.totalDebt||0)}</p>
                  <button onClick={()=>{setPayTarget(s);setPayType('supplier');}} className="text-xs text-blue-600 mt-1">View Account</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── EXPENSES ──────────────────────────────────────────────────────── */}
      {tab==='expenses' && (
        <div>
          <div className="flex flex-wrap gap-3 mb-3">
            <Select value={expCat} onChange={e=>{setEC(e.target.value);setPage(1);}} className="w-40">
              <option value="">All Categories</option>
              {EXP_CATS.map(c=><option key={c} value={c}>{c}</option>)}
            </Select>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b"><tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Category</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Description</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-red-500">Amount</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">By</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-50">
                {loading?Array(5).fill(0).map((_,i)=><tr key={i}><td colSpan={6} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse"/></td></tr>):
                  expenses.length===0?<tr><td colSpan={6} className="text-center py-10 text-gray-400">No expenses</td></tr>:
                  expenses.map(e=>(
                    <tr key={e._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3"><Badge color="orange">{e.category}</Badge></td>
                      <td className="px-4 py-3 text-gray-600">{e.description||'-'}</td>
                      <td className="px-4 py-3 text-right font-bold text-red-600">{formatCurrency(e.amount)}</td>
                      <td className="px-4 py-3 text-xs text-gray-400">{toJalali(e.date)}</td>
                      <td className="px-4 py-3 text-xs text-gray-400">{e.createdBy?.name||'-'}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex gap-1 justify-center">
                          <button onClick={()=>{setEditExp(e);setExpForm({category:e.category,amount:e.amount,description:e.description||'',date:e.date?e.date.split('T')[0]:''});setExpModal(true);}}
                            className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600">✏️</button>
                          <button onClick={()=>setDelExp(e)} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600">🗑️</button>
                        </div>
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
            <Pagination total={expTotal}/>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      <PaymentModal isOpen={!!payTarget} onClose={()=>setPayTarget(null)} target={payTarget} type={payType}
        onSuccess={()=>{ if(payType==='customer') fetchCustomers(); else fetchSuppliers(); fetchSummary(); }}/>

      {/* Funding Source Pre-Modal */}
      <FundingSourceModal
        isOpen={fundingModalOpen}
        onClose={()=>setFMOpen(false)}
        title="Select Funding Source for Expense"
        onSelect={(src)=>{
          setPFunding(src);
          setFMOpen(false);
          setEditExpense(null);
          resetExpense({ fundingSource: src.key, paidBy: src.label });
          setExpenseOpen(true);
        }}
      />

      {/* Expense Modal */}
      <Modal isOpen={expModal} onClose={()=>{setExpModal(false);setEditExp(null);}} title={editExp?'Edit Expense':'Record Expense'}>
        <div className="space-y-4">
          <FormField label="Category" required>
            <Select value={expForm.category} onChange={e=>setExpForm({...expForm,category:e.target.value})}>
              <option value="">Select category</option>
              {EXP_CATS.map(c=><option key={c} value={c}>{c}</option>)}
            </Select>
          </FormField>
          <FormField label="Amount" required>
            <Input type="number" step="0.01" min="0" value={expForm.amount} onChange={e=>setExpForm({...expForm,amount:e.target.value})} placeholder="0.00"/>
          </FormField>
          <FormField label="Date">
            <JalaliDatePicker value={expForm.date} onChange={v=>setExpForm({...expForm,date:v})} placeholder="Select date" className="w-full"/>
          </FormField>
          <FormField label="Description">
            <textarea value={expForm.description} onChange={e=>setExpForm({...expForm,description:e.target.value})} rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Description"/>
          </FormField>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={()=>{setExpModal(false);setEditExp(null);}}>Cancel</Button>
            <Button onClick={handleSaveExp} loading={savingExp}>{editExp?'Update':'Save'}</Button>
          </div>
        </div>
      </Modal>

      {/* Delete Expense Confirm */}
      <ConfirmModal isOpen={!!delExp} onClose={()=>setDelExp(null)} onConfirm={handleDelExp} loading={deletingExp}
        title="Delete Expense" message={`Delete expense "${delExp?.category} — ${formatCurrency(delExp?.amount)}"?`}/>
    </div>
  );
}
